import { CourseTag, LessonBlockKind, type LessonBlock } from './_models';

export type DayStamp = { daysAgo: number; time: string };

export type MockLesson = { title: string; durationMinutes: number; blocks: LessonBlock[] };

export type MockModule = { title: string; lessons: MockLesson[] };

export type MockCourse = {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  tag: CourseTag;
  coverAlt: string;
  learningGoals: string[];
  publishedAt: DayStamp;
  updatedAt: DayStamp;
  memberIds?: string[];
  modules: MockModule[];
};

export type MockTeamMember = {
  id: string;
  name: string;
  initials: string;
  role: string;
  doneLessonCounts: (number | null)[];
};

export type MockCourseActivity = { startedAt?: DayStamp; lastActivityAt?: DayStamp; completedAt?: DayStamp };

export const MOCK_CURRENT_MEMBER_ID = 'mariana';

export const MOCK_VIDEO_EMBED_URL = 'https://www.youtube-nocookie.com/embed/qIHXpnASPAA';

const RECEIVING_GUIDE_PDF_NAME = 'Passo a passo do recebimento.pdf';
const RECEIVING_GUIDE_PDF_URL = '/mock/trainings/passo-a-passo-do-recebimento.pdf';
const DAMAGE_REPORT_PDF_NAME = 'Termo de avaria (modelo).pdf';
const DAMAGE_REPORT_PDF_URL = '/mock/trainings/termo-de-avaria-modelo.pdf';

function videoBlock(title: string): LessonBlock {
  return { kind: LessonBlockKind.VIDEO, title, embedUrl: MOCK_VIDEO_EMBED_URL };
}

function videoLesson(title: string, durationMinutes: number): MockLesson {
  return { title, durationMinutes, blocks: [videoBlock(title)] };
}

function textLesson(title: string, durationMinutes: number): MockLesson {
  const textBlock: LessonBlock = {
    kind: LessonBlockKind.TEXT,
    paragraphs: ['Leia com calma e, quando terminar, marque a aula como concluída para seguir adiante.'],
    bullets: [
      'Siga a rotina da loja na ordem apresentada.',
      'Registre no sistema o que for conferido.',
      'Em caso de dúvida, fale com o gerente da loja.',
    ],
  };
  return { title, durationMinutes, blocks: [textBlock] };
}

const damageLesson: MockLesson = {
  title: 'Falta, sobra e avaria',
  durationMinutes: 10,
  blocks: [
    videoBlock('Falta, sobra e avaria'),
    {
      kind: LessonBlockKind.TEXT,
      paragraphs: [
        'O que fazer quando a mercadoria que chegou não bate com a nota: registrar a diferença no recebimento, separar o que veio avariado e avisar o fornecedor antes de lançar no estoque.',
      ],
      bullets: [
        'Falta: recebe a quantidade que chegou e deixa a diferença registrada.',
        'Sobra: não entra no estoque sem a nota correspondente.',
        'Avaria: foto, termo assinado pelo motorista e separação na área de devolução.',
      ],
    },
    {
      kind: LessonBlockKind.ATTACHMENTS,
      attachments: [{ name: DAMAGE_REPORT_PDF_NAME, typeLabel: 'PDF', sizeLabel: '1 KB', url: DAMAGE_REPORT_PDF_URL }],
    },
  ],
};

const confirmationLesson: MockLesson = {
  title: 'Confirmando o recebimento',
  durationMinutes: 9,
  blocks: [
    {
      kind: LessonBlockKind.TEXT,
      paragraphs: [
        'Com a nota conferida, falta confirmar o recebimento no EasyPDV. É essa confirmação que lança as quantidades no depósito e fecha a ordem de compra.',
      ],
      bullets: [
        'Abra a ordem de compra pela tela de recebimento e confira o depósito de destino.',
        'Ajuste as quantidades recebidas que ficaram diferentes da nota.',
        'Confirme. O saldo do depósito muda na hora, e a ordem fica como recebida.',
      ],
      notice: 'Uma nota que chegar depois não lança o estoque de novo: o recebimento já confirmado vale.',
    },
    { kind: LessonBlockKind.PDF, fileName: RECEIVING_GUIDE_PDF_NAME, url: RECEIVING_GUIDE_PDF_URL },
    {
      kind: LessonBlockKind.ATTACHMENTS,
      attachments: [
        { name: RECEIVING_GUIDE_PDF_NAME, typeLabel: 'PDF', sizeLabel: '1 KB', url: RECEIVING_GUIDE_PDF_URL },
        { name: DAMAGE_REPORT_PDF_NAME, typeLabel: 'PDF', sizeLabel: '1 KB', url: DAMAGE_REPORT_PDF_URL },
      ],
    },
  ],
};

export const MOCK_COURSES: MockCourse[] = [
  {
    id: 'primeiros-passos',
    title: 'Primeiros passos no EasyPDV',
    shortTitle: 'Primeiros passos',
    description: 'Conheça o painel, o cadastro de produtos e as rotinas básicas para começar a operar a loja.',
    tag: CourseTag.TECHNOLOGY,
    coverAlt: 'Corredor de caixas de um supermercado',
    learningGoals: [
      'Navegar pelo painel e pelo menu do EasyPDV',
      'Cadastrar produtos com os dados essenciais',
      'Registrar as vendas do dia a dia',
      'Saber onde pedir ajuda quando precisar',
    ],
    publishedAt: { daysAgo: 59, time: '08:00' },
    updatedAt: { daysAgo: 59, time: '08:00' },
    modules: [
      {
        title: 'Conhecendo o painel',
        lessons: [
          videoLesson('Entrando no EasyPDV', 8),
          videoLesson('Um passeio pelo menu', 6),
          textLesson('Seu perfil e suas permissões', 9),
        ],
      },
      {
        title: 'Cadastro de produtos',
        lessons: [videoLesson('Cadastrando um produto', 7), textLesson('Preços e códigos de barras', 8)],
      },
      {
        title: 'Vendas no dia a dia',
        lessons: [videoLesson('Acompanhando as vendas', 6), videoLesson('Fechando o caixa', 10)],
      },
      {
        title: 'Pedindo ajuda',
        lessons: [textLesson('Onde tirar dúvidas', 8), videoLesson('Novidades do sistema', 8)],
      },
    ],
  },
  {
    id: 'recebimento',
    title: 'Recebimento de mercadorias',
    shortTitle: 'Recebimento',
    description:
      'Como receber a mercadoria do fornecedor sem erro: da agenda de entregas à conferência da nota e ao lançamento no depósito certo.',
    tag: CourseTag.OPERATIONS,
    coverAlt: 'Motorista descarregando caixas de um caminhão de entregas',
    learningGoals: [
      'Organizar a agenda de entregas e a área de recebimento',
      'Conferir a nota contra a ordem de compra, item a item',
      'Registrar falta, sobra e avaria do jeito certo',
      'Confirmar o recebimento e lançar no depósito certo',
    ],
    publishedAt: { daysAgo: 35, time: '08:00' },
    updatedAt: { daysAgo: 12, time: '08:00' },
    modules: [
      {
        title: 'Antes de o caminhão chegar',
        lessons: [
          videoLesson('Agenda de entregas e janelas de recebimento', 6),
          videoLesson('Conferindo a ordem de compra', 8),
          textLesson('Preparando a área de recebimento', 5),
        ],
      },
      {
        title: 'Conferência da nota',
        lessons: [
          videoLesson('Localizando a nota pelo CNPJ', 7),
          videoLesson('Comparando itens e quantidades', 12),
          damageLesson,
        ],
      },
      {
        title: 'Lançamento no estoque',
        lessons: [confirmationLesson, videoLesson('Depósito certo e etiquetas', 8)],
      },
    ],
  },
  {
    id: 'abastecimento',
    title: 'Abastecimento da loja',
    shortTitle: 'Abastecimento',
    description: 'Do depósito à gôndola: prioridades, validade e reposição pelo picklist.',
    tag: CourseTag.OPERATIONS,
    coverAlt: 'Corredor de mercearia com prateleiras cheias',
    learningGoals: [
      'Levar a mercadoria do depósito à gôndola',
      'Respeitar a validade e a ordem das prateleiras',
      'Registrar o que foi reposto sem retrabalho',
    ],
    publishedAt: { daysAgo: 28, time: '08:00' },
    updatedAt: { daysAgo: 28, time: '08:00' },
    modules: [
      {
        title: 'Do depósito à gôndola',
        lessons: [
          videoLesson('O que repor primeiro', 8),
          videoLesson('Lendo o picklist', 9),
          textLesson('Separando por corredor', 7),
        ],
      },
      {
        title: 'Validade e organização',
        lessons: [
          videoLesson('Primeiro que vence, primeiro que sai', 6),
          videoLesson('Organizando a gôndola', 12),
          textLesson('Conferindo o que foi reposto', 8),
        ],
      },
    ],
  },
  {
    id: 'checklist',
    title: 'Checklist de abertura e fechamento',
    shortTitle: 'Checklist',
    description: 'O que conferir antes de abrir as portas e depois de fechar o último caixa.',
    tag: CourseTag.OPERATIONS,
    coverAlt: 'Seção de frutas e verduras de um supermercado',
    learningGoals: [
      'Seguir a rotina de abertura da loja',
      'Fazer o fechamento sem esquecer nenhuma etapa',
      'Registrar o que ficou pendente para o próximo turno',
    ],
    publishedAt: { daysAgo: 2, time: '08:00' },
    updatedAt: { daysAgo: 2, time: '08:00' },
    modules: [
      {
        title: 'Abertura da loja',
        lessons: [
          videoLesson('Antes de abrir as portas', 6),
          textLesson('Conferindo o caixa inicial', 8),
          videoLesson('Ligando os equipamentos', 7),
        ],
      },
      {
        title: 'Fechamento da loja',
        lessons: [videoLesson('Fechando o último caixa', 10), textLesson('Deixando a loja pronta para amanhã', 9)],
      },
    ],
  },
  {
    id: 'impressoras',
    title: 'Impressoras e etiquetas',
    shortTitle: 'Impressoras',
    description: 'Trocar o ribbon, alinhar a etiqueta e imprimir as etiquetas de gôndola em lote.',
    tag: CourseTag.TECHNOLOGY,
    coverAlt: 'Impressora de etiquetas Honeywell PC42T-D',
    learningGoals: [
      'Trocar o ribbon da impressora de etiquetas',
      'Alinhar a etiqueta ao imprimir',
      'Imprimir etiquetas de gôndola em lote',
    ],
    publishedAt: { daysAgo: 1, time: '08:00' },
    updatedAt: { daysAgo: 1, time: '08:00' },
    modules: [
      { title: 'Trocando o ribbon', lessons: [videoLesson('Trocando o ribbon', 8)] },
      { title: 'Alinhando a etiqueta', lessons: [videoLesson('Alinhando a etiqueta', 9)] },
      { title: 'Etiquetas de gôndola', lessons: [textLesson('Imprimindo etiquetas em lote', 8)] },
    ],
  },
  {
    id: 'financas',
    title: 'Boas práticas financeiras',
    shortTitle: 'Financeiro',
    description: 'Controle de caixa, contas a pagar e a receber e os indicadores que mostram a saúde da loja.',
    tag: CourseTag.FINANCE,
    coverAlt: 'Calculadora sobre uma mesa de trabalho',
    learningGoals: [
      'Organizar o financeiro da loja',
      'Manter a rotina do caixa em dia',
      'Acompanhar contas a pagar e a receber',
      'Ler os indicadores que importam',
    ],
    publishedAt: { daysAgo: 16, time: '08:00' },
    updatedAt: { daysAgo: 16, time: '08:00' },
    memberIds: ['mariana', 'rafael'],
    modules: [
      {
        title: 'Organização financeira',
        lessons: [
          videoLesson('Por que separar as contas', 10),
          textLesson('Categorias e centros de custo', 9),
          videoLesson('Lançando uma despesa', 12),
        ],
      },
      {
        title: 'Rotina do caixa',
        lessons: [textLesson('Conferência diária', 8), videoLesson('Sangria e fechamento do caixa', 11)],
      },
      {
        title: 'Contas a pagar e a receber',
        lessons: [
          videoLesson('Programando pagamentos', 10),
          textLesson('Conciliando recebimentos', 12),
          videoLesson('Lidando com atrasos', 9),
        ],
      },
      {
        title: 'Indicadores',
        lessons: [videoLesson('Lendo o DRE', 10), textLesson('Margem e ponto de equilíbrio', 8)],
      },
      {
        title: 'Planejamento',
        lessons: [videoLesson('Fluxo de caixa projetado', 11), textLesson('Metas para o próximo mês', 10)],
      },
    ],
  },
];

export const MOCK_TEAM: MockTeamMember[] = [
  {
    id: 'mariana',
    name: 'Mariana Costa',
    initials: 'MC',
    role: 'Franqueada',
    doneLessonCounts: [9, 5, 0, 0, 0, 3],
  },
  { id: 'rafael', name: 'Rafael Lima', initials: 'RL', role: 'Gerente', doneLessonCounts: [9, 8, 6, 2, 0, 6] },
  {
    id: 'juliana',
    name: 'Juliana Souza',
    initials: 'JS',
    role: 'Operadora de caixa',
    doneLessonCounts: [9, 0, 0, 5, 1, null],
  },
  { id: 'diego', name: 'Diego Alves', initials: 'DA', role: 'Repositor', doneLessonCounts: [9, 2, 6, 0, 0, null] },
  {
    id: 'camila',
    name: 'Camila Rocha',
    initials: 'CR',
    role: 'Operadora de caixa',
    doneLessonCounts: [4, 0, 0, 0, 0, null],
  },
  {
    id: 'bruno',
    name: 'Bruno Teixeira',
    initials: 'BT',
    role: 'Estoquista',
    doneLessonCounts: [0, 0, 0, 0, 0, null],
  },
];

export const MOCK_CURRENT_MEMBER_ACTIVITY: Record<string, MockCourseActivity> = {
  recebimento: {
    startedAt: { daysAgo: 10, time: '08:30' },
    lastActivityAt: { daysAgo: 0, time: '09:40' },
  },
  financas: {
    startedAt: { daysAgo: 12, time: '10:00' },
    lastActivityAt: { daysAgo: 4, time: '17:05' },
  },
  'primeiros-passos': {
    startedAt: { daysAgo: 40, time: '09:00' },
    lastActivityAt: { daysAgo: 15, time: '11:00' },
    completedAt: { daysAgo: 15, time: '11:00' },
  },
};
